/**
 * React Hook for S3 Service
 * Provides easy access to S3 operations with loading states and error handling
 */
import { useState, useEffect, useCallback } from 'react';
import { getS3Service, S3ServiceError } from '../services/S3Service';
import { SignedUrlValidator } from '../utils/signedUrlValidator';
import { useAuth } from '../contexts/AuthContext';
/**
 * Main S3 Service hook
 * Provides access to S3Service with authentication state management
 */
export function useS3Service() {
    const [s3Service, setS3Service] = useState(null);
    const [isReady, setIsReady] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const { currentUser } = useAuth();
    useEffect(() => {
        const initializeService = async () => {
            setLoading(true);
            setError(null);
            try {
                if (currentUser) {
                    const service = getS3Service();
                    // Test the service by checking credentials
                    const hasCredentials = await service.refreshCredentials();
                    setS3Service(service);
                    setIsReady(true);
                }
                else {
                    setS3Service(null);
                    setIsReady(false);
                    setError('User not authenticated');
                }
            }
            catch (err) {
                console.error('Failed to initialize S3Service:', err);
                setError(err instanceof Error ? err.message : 'Failed to initialize S3 service');
                setS3Service(null);
                setIsReady(false);
            }
            finally {
                setLoading(false);
            }
        };
        initializeService();
    }, [currentUser]);
    return {
        s3Service,
        isAuthenticated: isReady,
        error,
        loading,
    };
}
/**
 * Hook for listing S3 objects with pagination
 */
export function useS3List(prefix = '', autoLoad = true) {
    const { s3Service, isAuthenticated } = useS3Service();
    const [items, setItems] = useState([]);
    const [nextContinuationToken, setNextContinuationToken] = useState();
    const [isTruncated, setIsTruncated] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const loadObjects = useCallback(async (resetList = true) => {
        if (!s3Service || !isAuthenticated)
            return;
        setLoading(true);
        setError(null);
        try {
            const result = await s3Service.listObjects(prefix, '/', 100, resetList ? undefined : nextContinuationToken);
            if (resetList) {
                setItems(result.items);
            }
            else {
                setItems(prev => [...prev, ...result.items]);
            }
            setNextContinuationToken(result.nextContinuationToken);
            setIsTruncated(result.isTruncated);
            setTotalCount(resetList ? result.totalCount : totalCount + result.totalCount);
        }
        catch (err) {
            console.error('Failed to load S3 objects:', err);
            setError(err instanceof S3ServiceError ? err.message : 'Failed to load objects');
        }
        finally {
            setLoading(false);
        }
    }, [s3Service, isAuthenticated, prefix, nextContinuationToken, totalCount]);
    const refresh = useCallback(() => loadObjects(true), [loadObjects]);
    const loadMore = useCallback(() => loadObjects(false), [loadObjects]);
    useEffect(() => {
        if (autoLoad && isAuthenticated) {
            loadObjects(true);
        }
    }, [autoLoad, isAuthenticated, loadObjects]);
    return {
        items,
        nextContinuationToken,
        isTruncated,
        totalCount,
        loading,
        error,
        refresh,
        loadMore,
    };
}
/**
 * Hook for downloading files from S3
 */
export function useS3Download() {
    const { s3Service, isAuthenticated } = useS3Service();
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState(null);
    const downloadFile = useCallback(async (key, filename) => {
        if (!s3Service || !isAuthenticated) {
            throw new Error('S3 service not ready');
        }
        setDownloading(true);
        setError(null);
        try {
            // Get the file blob
            const blob = await s3Service.downloadObject(key);
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename || key.split('/').pop() || 'download';
            // Trigger download
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            // Clean up
            window.URL.revokeObjectURL(url);
        }
        catch (err) {
            console.error('Download failed:', err);
            const errorMessage = err instanceof S3ServiceError ? err.message : 'Download failed';
            setError(errorMessage);
            throw new Error(errorMessage);
        }
        finally {
            setDownloading(false);
        }
    }, [s3Service, isAuthenticated]);
    const generateDownloadUrl = useCallback(async (key, options) => {
        if (!s3Service || !isAuthenticated) {
            return {
                isValid: false,
                errors: ['S3 service not ready'],
                warnings: [],
            };
        }
        setError(null);
        try {
            const validator = new SignedUrlValidator(s3Service);
            const result = await validator.generateDownloadSignedUrl(key, undefined, options?.expirationMinutes);
            if (!result.isValid) {
                setError(result.errors.join(', '));
            }
            return result;
        }
        catch (err) {
            console.error('Failed to generate download URL:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to generate download URL';
            setError(errorMessage);
            return {
                isValid: false,
                errors: [errorMessage],
                warnings: [],
            };
        }
    }, [s3Service, isAuthenticated]);
    return {
        downloadFile,
        generateDownloadUrl,
        downloading,
        error,
    };
}
/**
 * Hook for checking S3 object existence and metadata
 */
export function useS3ObjectInfo(key) {
    const { s3Service, isAuthenticated } = useS3Service();
    const [exists, setExists] = useState(null);
    const [metadata, setMetadata] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => {
        const checkObject = async () => {
            if (!s3Service || !isAuthenticated || !key)
                return;
            setLoading(true);
            setError(null);
            try {
                const objectExists = await s3Service.objectExists(key);
                setExists(objectExists);
                if (objectExists) {
                    const objectMetadata = await s3Service.getObjectMetadata(key);
                    setMetadata(objectMetadata);
                }
                else {
                    setMetadata(null);
                }
            }
            catch (err) {
                console.error('Failed to check object:', err);
                setError(err instanceof S3ServiceError ? err.message : 'Failed to check object');
                setExists(false);
                setMetadata(null);
            }
            finally {
                setLoading(false);
            }
        };
        checkObject();
    }, [s3Service, isAuthenticated, key]);
    return {
        exists,
        metadata,
        loading,
        error,
    };
}
export default {
    useS3Service,
    useS3List,
    useS3Download,
    useS3ObjectInfo,
};
